import { useState, useEffect, useRef } from 'react';

/**
 * Onboarding Step 4: First Analysis.
 * Per wireframe OnboardingC — live log with animated progress, then CTA.
 *
 * On mount: triggers research run, polls until complete.
 */
export function OnboardingStep4({ config, portfolioId, agentName, bucket, jwtToken, onComplete }) {
  const [logLines, setLogLines] = useState([]);
  const [ticketId, setTicketId] = useState(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const addLog = (icon, color, text) => {
    setLogLines(prev => [...prev, { icon, color, text }]);
  };

  // Run research on mount
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      addLog('✓', '#81c995', `agent "${agentName}" created`);
      addLog('✓', '#81c995', `paper portfolio linked · $10,000 starting cash`);
      addLog('✓', '#81c995', `research config: ${bucket || 'Momentum'}`);
      addLog('✓', '#81c995', `recommendation config: balanced risk`);

      // Trigger research run
      try {
        addLog('▸', '#fbbc04', 'initiating research run...');

        const res = await fetch(`${config.apiBaseUrl}/portfolio/${portfolioId}/research/run`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });

        if (!res.ok) {
          // Research may fail if no config; show a graceful fallback
          addLog('▸', '#fbbc04', 'research setup queued — you can run it from the Research tab');
          if (!cancelled) setDone(true);
          return;
        }

        const data = await res.json();
        const tid = data.ticket_id;
        if (!cancelled) setTicketId(tid);

        addLog('▸', '#fbbc04', 'scanning tokens for signals...');

        // Poll for completion
        const poll = async () => {
          if (cancelled) return;
          try {
            const pollRes = await fetch(`${config.apiBaseUrl}/portfolio/${portfolioId}/research/poll/${tid}`, {
              headers: { Authorization: `Bearer ${jwtToken}` },
            });
            if (pollRes.ok) {
              const pollData = await pollRes.json();
              if (pollData.status === 'COMPLETED') {
                const tokens = pollData.result?.proposed_tokens || [];
                addLog('▸', '#fbbc04', `${tokens.length} candidates found: ${tokens.join(' · ') || 'analyzing...'}`);
                addLog('✓', '#81c995', 'analysis complete!');
                if (!cancelled) setDone(true);
                return;
              }
              if (pollData.status === 'FAILED') {
                addLog('▸', '#fbbc04', 'analysis queued — check the Research tab');
                if (!cancelled) setDone(true);
                return;
              }
            }
            // Keep polling
            pollRef.current = setTimeout(poll, 3000);
          } catch {
            addLog('▸', '#fbbc04', 'research running in background...');
            if (!cancelled) setDone(true);
          }
        };
        pollRef.current = setTimeout(poll, 3000);

      } catch (err) {
        addLog('▸', '#fbbc04', 'research queued — you can run it from the Research tab');
        if (!cancelled) setDone(true);
      }
    };

    run();

    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-4 mt-2">
      {/* Live log */}
      <div className="bg-slate-950 rounded-lg p-4 font-mono text-xs leading-relaxed border border-slate-700 max-h-48 overflow-y-auto">
        {logLines.map((line, i) => (
          <div key={i}>
            <span style={{ color: line.color }}>{line.icon}</span> {line.text}
          </div>
        ))}
        {!done && (
          <div className="opacity-50">
            <span style={{ color: '#fbbc04' }}>▸</span> processing<span className="animate-pulse"> ▏</span>
          </div>
        )}
      </div>

      {/* What happens next */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-white">What happens next</div>
          {!done && <span className="text-xs text-slate-400">~15 sec</span>}
        </div>
        <hr className="border-slate-700 border-dashed" />
        <div className="text-sm text-slate-300 space-y-1">
          <div>→ You'll land in your <span className="font-semibold">workspace</span> with a fresh recommendation</div>
          <div>→ Review it, tweak if you want, click <span className="font-semibold">Execute</span> for a paper trade</div>
          <div>→ Watch the position appear in real time on the Performance tab</div>
        </div>
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <hr className="border-slate-700 border-dashed" />

      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">
          {done ? 'your workspace is ready! 🎯' : "you're ~15 sec from your first paper trade 🎯"}
        </span>
        <button
          onClick={onComplete}
          disabled={!done}
          className={`px-4 py-2 rounded text-sm font-medium text-white ${
            done ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-slate-700 opacity-50 cursor-not-allowed'
          }`}
        >
          {done ? 'go to workspace →' : 'waiting…'}
        </button>
      </div>
    </div>
  );
}
